import { useEffect } from 'react';
import { notification, Tag } from 'antd';
import { RobotOutlined } from '@ant-design/icons';
import { useNotifications } from '../../contexts/NotificationContext';
import { ApplicationStatus } from '../../types';
import { maybeShowRejectionEncouragement } from '../../utils/rejectionEncouragement';

const statusDisplayNames: Record<ApplicationStatus, string> = {
  submitted: 'Submitted',
  under_review: 'Under Review',
  screening_call: 'Screening Call',
  first_interview: 'First Interview',
  second_interview: 'Second Interview',
  additional_interview: 'Additional Interview',
  technical_assessment: 'Technical Assessment',
  reference_check: 'Reference Check',
  offer_extended: 'Offer Extended',
  negotiation: 'Negotiation',
  hired: 'Hired',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
};

const statusColors: Record<ApplicationStatus, string> = {
  submitted: 'blue',
  under_review: 'gold',
  screening_call: 'green',
  first_interview: 'green',
  second_interview: 'green',
  additional_interview: 'green',
  technical_assessment: 'green',
  reference_check: 'green',
  offer_extended: 'purple',
  negotiation: 'purple',
  hired: 'gold',
  rejected: 'red',
  withdrawn: 'default',
};

const ApplicationStatusNotifier: React.FC = () => {
  const { subscribe } = useNotifications();

  useEffect(() => {
    return subscribe(['application_status_changed'], (event) => {
      const { metadata } = event;
      const newStatus = metadata?.new_status as ApplicationStatus | undefined;
      const autoDetected = metadata?.auto_detected === true;
      const reasoning = metadata?.reasoning as string | undefined;

      const description = (
        <div>
          <div>{event.message}</div>
          {newStatus && (
            <div style={{ marginTop: 8 }}>
              <Tag color={statusColors[newStatus]}>
                {statusDisplayNames[newStatus] || newStatus}
              </Tag>
            </div>
          )}
          {reasoning && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
              {reasoning}
            </div>
          )}
        </div>
      );

      notification.info({
        message: (
          <span>
            {event.title}
            {autoDetected && (
              <Tag
                icon={<RobotOutlined />}
                color="processing"
                style={{ marginLeft: 8, fontSize: 11 }}
              >
                Auto-detected
              </Tag>
            )}
          </span>
        ),
        description,
        placement: 'topRight',
        duration: 8,
      });

      // First-rejection reassurance — shared with the manual status-dropdown path
      // so both trigger identical copy and share the one-time cap.
      maybeShowRejectionEncouragement(
        newStatus,
        event.entityType === 'application' ? event.entityId : undefined
      );
    });
  }, [subscribe]);

  return null;
};

export default ApplicationStatusNotifier;
